import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, vShow as _vShow, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "modal-mask"
}
const _hoisted_2 = { class: "modal-wrapper" }
const _hoisted_3 = {
  class: "modal-dialog modal-dialog-scrollable",
  style: {"max-width":"96%"}
}
const _hoisted_4 = { class: "modal-content" }
const _hoisted_5 = { class: "modal-header" }
const _hoisted_6 = { class: "modal-title" }
const _hoisted_7 = { class: "modal-body" }
const _hoisted_8 = { key: 0 }
const _hoisted_9 = { key: 1 }
const _hoisted_10 = { class: "d-flex justify-content-between" }
const _hoisted_11 = { class: "dropdown" }
const _hoisted_12 = {
  class: "btn btn-outline-secondary dropdown-toggle",
  "data-toggle": "dropdown"
}
const _hoisted_13 = {
  class: "dropdown-menu",
  style: {"max-height":"300px","overflow-y":"auto"}
}
const _hoisted_14 = ["onClick"]
const _hoisted_15 = { class: "dropdown d-inline-block" }
const _hoisted_16 = {
  class: "btn btn-outline-secondary dropdown-toggle",
  "data-toggle": "dropdown"
}
const _hoisted_17 = { class: "dropdown-menu" }

import { computed, watch, nextTick, toRef } from 'vue'

    import { KendoChart, KendoChartDataSerie, KendoChartOptions } from '@Kendo/Components/KendoChart'
    import { getDistinctColor, numCompare, roundDecimal, splitString } from '@common/Common'

    import type {
        WeightsViewerCriterionReadModel,
        WeightsViewerReadModel
    } from '@Criteria/Weights/Domain/readmodels'

    // ---------------- local jquery ----------------

    
export default /*@__PURE__*/_defineComponent({
  __name: 'WeightsViewerDialog',
  props: {
    propOpened: { type: Boolean },
    propRm: {}
  },
  emits: ["export-weightsset-weights", "export-survey-weights", "export-panelist-weights", "close-dialog"],
  setup(__props: any, { emit: __emit }) {

    const $ = ((window as any).kendo?.jQuery ?? (window as any).$) as JQueryStatic

    // ---------------- props / emits ----------------

    const props = __props

    const emit = __emit

    // ---------------- shortcuts ----------------

    const opened = toRef(props, 'propOpened')
    const rm = toRef(props, 'propRm')

    // ---------------- local state ----------------

    let PieChart: KendoChart | null = null
    let BarChart: KendoChart | null = null

    // ---------------- computed ----------------

    const selectedCriterionId = computed(() => rm.value?.SelectedCriterionId)

    const selectedCriterion = computed<WeightsViewerCriterionReadModel | null>(() =>
        rm.value?.Criteria.find(c => c.Id === selectedCriterionId.value) ?? null
    )

    const dataVizType = computed(() => rm.value?.DataVizType ?? null)
    const precision = computed(() => rm.value?.Precision ?? null)

    const precisionLabel = computed(() =>
        precision.value != null
            ? `${1 / Math.pow(10, precision.value)}%`
            : ''
    )

    const Weights = computed(() => rm.value?.Criteria ?? [])

    const Parents = computed(() =>
        Weights.value.filter(a => a.IsParent === true)
    )

    const Subcriteria = computed(() =>
        Weights.value.filter(a => a.ParentId === selectedCriterionId.value)
    )

    const Bottom = computed(() =>
        GetChildlessDescendantCriteria(selectedCriterionId.value!, Weights.value)
    )

    // ---------------- watchers (replaces @Component.watch) ----------------

    watch(selectedCriterion, val =>
    {
        if (val)
        {
            nextTick(RenderDataViz)
        }
    })

    watch(precision, (_, oldVal) =>
    {
        if (oldVal != null)
        {
            RenderDataViz()
        }
    })

    watch(dataVizType, (_, oldVal) =>
    {
        if (oldVal != null)
        {
            nextTick(RenderDataViz)
        }
    })

    // ---------------- helpers ----------------

    function getLocalWeight(w: WeightsViewerCriterionReadModel): number
    {
        return roundDecimal(w.LocalWeight, precision.value!)
    }

    function getGlobalWeight(w: WeightsViewerCriterionReadModel): number
    {
        return roundDecimal(w.GlobalWeight, precision.value!)
    }

    function getLocalWeightLabel(w: WeightsViewerCriterionReadModel): string
    {
        return splitString(`${w.Name} (${getLocalWeight(w)}%)`, 30).join('\n')
    }

    function getGlobalWeightLabel(w: WeightsViewerCriterionReadModel): string
    {
        return splitString(`${w.Name} (${getGlobalWeight(w)}%)`, 30).join('\n')
    }

    function getCriterionNameMultiline(w: WeightsViewerCriterionReadModel): string
    {
        return splitString(w.Name, 40).join('\n')
    }

    // ---------------- actions ----------------

    function SetCriterion(id: number)
    {
        rm.value!.SelectedCriterionId = id
    }

    function SetPrecision(p: number)
    {
        rm.value!.Precision = p
    }

    function SwitchToChart()
    {
        rm.value!.DataVizType = 'Chart'
    }

    function SwitchToGrid()
    {
        rm.value!.DataVizType = 'Grid'
    }

    // ---------------- grid ----------------

    function GetGridData(contextId: number)
    {
        return Weights.value
            .filter(a => a.ParentId === contextId)
            .map(w => ({
                CriterionId: w.Id,
                Name: w.Name,
                LocalWeight: getLocalWeight(w),
                LocalWeightProgressBarValue: w.LocalWeightProgressBarValue,
                GlobalWeight: getGlobalWeight(w),
                GlobalWeightProgressBarValue: w.GlobalWeightProgressBarValue,
                HasChildren: w.IsParent
            }))
    }

    function RenderGrid()
    {
        const data = GetGridData(selectedCriterionId.value!)

        const grid = $('#grid').data('kendoGrid')
        if (grid) grid.destroy()

        $('#grid').kendoGrid({
            dataSource: { data },
            height: 600,
            detailInit: e => GridDetailInit(e, GetGridData),
            dataBound: GridDataBound,
            resizable: true,
            sortable: true,
            columnMenu: true,
            filterable: true,
            columns: [
                { field: 'Name', title: 'Criterion', width: '220px' },
                { field: 'LocalWeight', title: 'Local Weight', width: '220px', template: GetLocalWeightColumnTemplate },
                { field: 'GlobalWeight', title: 'Global Weight', width: '220px', template: GetGlobalWeightColumnTemplate }
            ]
        })
    }

    // ---------------- charts ----------------

    function RenderPieChart()
    {
        const data = Subcriteria.value.map((w, i) => ({
            CriterionId: w.Id,
            Name: w.Name,
            Weight: getLocalWeight(w),
            Label: getLocalWeightLabel(w),
            Color: getDistinctColor(i)
        }))

        const options = new KendoChartOptions()
        options.SetTooltip(true, '#= category #: #= value #%')
        options.HideLegend()
        options.SetDonutSeriesType()

        const serie = new KendoChartDataSerie()
        serie.SetField('Weight')
        serie.SetCategoryField('Name')
        serie.SetColorField('Color')
        serie.SetLabels(true, '#= dataItem.Label #', '1.2rem Arial', 'outsideEnd')
        serie.SetLabelsAlign('circle')
        serie.EnableAutoFit()
        serie.SetData(data)

        options.SetOnSeriesClick(e =>
        {
            if (Weights.value.some(a => a.ParentId === e.dataItem.CriterionId))
            {
                SetCriterion(e.dataItem.CriterionId)
            }
        })

        PieChart = KendoChart.CreateChart(options)
        PieChart.SetDataSeries([serie])
        PieChart.Render1('PieChart')
    }

    function RenderBarChart()
    {
        const data = Subcriteria.value
            .flatMap((w, i) =>
            {
                const bottom = GetChildlessDescendantCriteria(w.Id, Weights.value)
                const list = bottom.length ? bottom : [w]

                return list.map(b => ({
                    CriterionId: b.Id,
                    Name: b.Name,
                    MultilineName: getCriterionNameMultiline(b),
                    Weight: getGlobalWeight(b),
                    Label: getGlobalWeightLabel(b),
                    Color: getDistinctColor(i)
                }))
            })
            .sort((a, b) => numCompare(a.Weight, b.Weight))
            .reverse()

        const options = new KendoChartOptions()
        options.SetTooltip(true, '#= dataItem.Name #: #= value #%')
        options.HideLegend()
        options.SetBarSeriesType()
        options.SetCategoryAxis(true, true, '1rem Arial')

        const serie = new KendoChartDataSerie()
        serie.SetField('Weight')
        serie.SetCategoryField('MultilineName')
        serie.SetColorField('Color')
        serie.SetData(data)

        BarChart = KendoChart.CreateChart(options)
        BarChart.SetDataSeries([serie])

        BarChart.SetHeight(Math.max(data.length * 100, 300))
        BarChart.Render1('BarChart')
    }

    function RenderDataViz()
    {
        if (dataVizType.value === 'Chart')
        {
            RenderPieChart()
            RenderBarChart()
        }
        else if (dataVizType.value === 'Grid')
        {
            RenderGrid()
        }
    }

    // ---------------- export / close ----------------

    function exportWeights()
    {
        if (rm.value?.WeightsSetId)
            emit('export-weightsset-weights', rm.value.WeightsSetId)
        else if (rm.value?.SurveyId)
            emit('export-survey-weights', rm.value.SurveyId)
        else if (rm.value?.ProjectUserId)
            emit('export-panelist-weights', rm.value.ProjectUserId)
    }

    function close()
    {
        emit('close-dialog')
    }

    // ---------------- pure helpers (unchanged logic) ----------------

    function GetChildlessDescendantCriteria(
        criterionId: number,
        all: WeightsViewerCriterionReadModel[]
    )
    {
        return GetDescendantCriteria(criterionId, all).filter(a => !a.IsParent)
    }

    function GetDescendantCriteria(
        criterionId: number,
        all: WeightsViewerCriterionReadModel[]
    ): WeightsViewerCriterionReadModel[]
    {
        const subs = all.filter(a => a.ParentId === criterionId)
        return subs.flatMap(s => [s, ...GetDescendantCriteria(s.Id, all)])
    }

    function GridDetailInit(e: any, getData: (id: number) => any[])
    {
        $('<div/>').appendTo(e.detailCell).kendoGrid({
            dataSource: { data: getData(e.data.CriterionId) },
            detailInit: ev => GridDetailInit(ev, getData),
            dataBound: GridDataBound,
            resizable: true,
            sortable: true,
            columnMenu: true,
            filterable: true,
            columns: [
                { field: 'Name', title: 'Criterion' },
                { field: 'LocalWeight', title: 'Local Weight', width: '200px', template: GetLocalWeightColumnTemplate },
                { field: 'GlobalWeight', title: 'Global Weight', width: '200px', template: GetGlobalWeightColumnTemplate }
            ]
        })
    }

    function GridDataBound(this: any)
    {
        const data = this.dataSource.data()
        for (const row of data)
        {
            if (row.get('HasChildren') === false)
            {
                $(`tr[data-uid="${row.uid}"]`).find('.k-hierarchy-cell a').hide()
            }
        }
    }

    function GetLocalWeightColumnTemplate(e: any): string
    {
        const value = e.LocalWeight
        const progress = e.LocalWeightProgressBarValue
        const textColorCss = progress < 10 ? 'text-dark' : ''

        return `<div class="progress" style="height:26px">
        <div class="progress-bar ${textColorCss}" style="width:${progress}%">${value}</div>
    </div>`
    }

    function GetGlobalWeightColumnTemplate(e: any): string
    {
        const value = e.GlobalWeight
        const progress = e.GlobalWeightProgressBarValue
        const textColorCss = progress < 10 ? 'text-dark' : ''

        return `<div class="progress" style="height:26px">
        <div class="progress-bar ${textColorCss}" style="width:${progress}%">${value}</div>
    </div>`
    }

return (_ctx: any,_cache: any) => {
  return (opened.value)
    ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("div", _hoisted_3, [
            _createElementVNode("div", _hoisted_4, [
              _createElementVNode("div", _hoisted_5, [
                _createElementVNode("h5", _hoisted_6, "Weights: " + _toDisplayString(rm.value?.Name), 1 /* TEXT */),
                _createElementVNode("button", {
                  onClick: close,
                  type: "button",
                  class: "close"
                }, "×")
              ]),
              _createElementVNode("div", _hoisted_7, [
                (!rm.value)
                  ? (_openBlock(), _createElementBlock("h2", _hoisted_8, [...(_cache[6] || (_cache[6] = [
                      _createElementVNode("i", { class: "fa fa-spinner fa-spin" }, null, -1 /* CACHED */),
                      _createTextVNode(" Loading...", -1 /* CACHED */)
                    ]))]))
                  : _createCommentVNode("v-if", true),
                (rm.value)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                      _createElementVNode("div", _hoisted_10, [
                        _createElementVNode("div", _hoisted_11, [
                          _createElementVNode("button", _hoisted_12, [
                            _createElementVNode("span", null, _toDisplayString(selectedCriterion.value?.Name), 1 /* TEXT */)
                          ]),
                          _createElementVNode("div", _hoisted_13, [
                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(Parents.value, (c) => {
                              return (_openBlock(), _createElementBlock("a", {
                                class: "dropdown-item",
                                key: c.Id,
                                onClick: ($event: any) => (SetCriterion(c.Id))
                              }, [
                                _createElementVNode("span", null, _toDisplayString(c.Name), 1 /* TEXT */),
                                _cache[7] || (_cache[7] = _createElementVNode("br", null, null, -1 /* CACHED */)),
                                _createElementVNode("small", null, _toDisplayString(c.Path), 1 /* TEXT */)
                              ], 8 /* PROPS */, _hoisted_14))
                            }), 128 /* KEYED_FRAGMENT */))
                          ])
                        ]),
                        _createElementVNode("div", null, [
                          _createElementVNode("div", _hoisted_15, [
                            _createElementVNode("button", _hoisted_16, _toDisplayString(precisionLabel.value), 1 /* TEXT */),
                            _createElementVNode("div", _hoisted_17, [
                              _cache[8] || (_cache[8] = _createElementVNode("h6", { class: "dropdown-header" }, "Precision:", -1 /* CACHED */)),
                              _createElementVNode("a", {
                                class: "dropdown-item",
                                onClick: _cache[0] || (_cache[0] = ($event: any) => (SetPrecision(0)))
                              }, "1"),
                              _createElementVNode("a", {
                                class: "dropdown-item",
                                onClick: _cache[1] || (_cache[1] = ($event: any) => (SetPrecision(1)))
                              }, "0.1"),
                              _createElementVNode("a", {
                                class: "dropdown-item",
                                onClick: _cache[2] || (_cache[2] = ($event: any) => (SetPrecision(2)))
                              }, "0.01"),
                              _createElementVNode("a", {
                                class: "dropdown-item",
                                onClick: _cache[3] || (_cache[3] = ($event: any) => (SetPrecision(3)))
                              }, "0.001"),
                              _createElementVNode("a", {
                                class: "dropdown-item",
                                onClick: _cache[4] || (_cache[4] = ($event: any) => (SetPrecision(4)))
                              }, "0.0001"),
                              _createElementVNode("a", {
                                class: "dropdown-item",
                                onClick: _cache[5] || (_cache[5] = ($event: any) => (SetPrecision(5)))
                              }, "0.00001")
                            ])
                          ]),
                          _createElementVNode("div", {
                            class: "btn-group",
                            role: "group"
                          }, [
                            _createElementVNode("button", {
                              type: "button",
                              class: "btn btn-outline-secondary",
                              onClick: SwitchToChart
                            }, [...(_cache[9] || (_cache[9] = [
                              _createElementVNode("i", {
                                class: "fa fa-pie-chart",
                                "aria-hidden": "true"
                              }, null, -1 /* CACHED */)
                            ]))]),
                            _createElementVNode("button", {
                              type: "button",
                              class: "btn btn-outline-secondary",
                              onClick: SwitchToGrid
                            }, [...(_cache[10] || (_cache[10] = [
                              _createElementVNode("i", {
                                class: "fa fa-table",
                                "aria-hidden": "true"
                              }, null, -1 /* CACHED */)
                            ]))])
                          ]),
                          _createElementVNode("button", {
                            type: "button",
                            class: "btn btn-outline-secondary",
                            onClick: exportWeights
                          }, [...(_cache[11] || (_cache[11] = [
                            _createElementVNode("i", { class: "fa fa-download fa-lg" }, null, -1 /* CACHED */)
                          ]))])
                        ])
                      ]),
                      _cache[14] || (_cache[14] = _createElementVNode("br", null, null, -1 /* CACHED */)),
                      _withDirectives(_createElementVNode("div", null, [...(_cache[12] || (_cache[12] = [
                        _createElementVNode("div", { id: "Chart-PieChart" }, null, -1 /* CACHED */),
                        _createElementVNode("div", { id: "Chart-BarChart" }, null, -1 /* CACHED */)
                      ]))], 512 /* NEED_PATCH */), [
                        [_vShow, dataVizType.value === 'Chart']
                      ]),
                      _withDirectives(_createElementVNode("div", null, [...(_cache[13] || (_cache[13] = [
                        _createElementVNode("div", { id: "grid" }, null, -1 /* CACHED */)
                      ]))], 512 /* NEED_PATCH */), [
                        [_vShow, dataVizType.value === 'Grid']
                      ]),
                      _cache[15] || (_cache[15] = _createElementVNode("br", null, null, -1 /* CACHED */))
                    ]))
                  : _createCommentVNode("v-if", true)
              ]),
              _createElementVNode("div", { class: "modal-footer" }, [
                _createElementVNode("button", {
                  onClick: close,
                  class: "btn btn-outline-secondary"
                }, "Close")
              ])
            ])
          ])
        ])
      ]))
    : _createCommentVNode("v-if", true)
}
}

})